// src/components/fiscal/CancelInvoiceModal.js
import React, { useState } from "react";
import { X, AlertTriangle, Send, Loader2 } from "lucide-react";
import { fiscalService } from "../../services/fiscalService";
import { pb } from "../../services/pocketbase";

export default function CancelInvoiceModal({ isOpen, onClose, invoice, config, onSuccess }) {
  const [justificativa, setJustificativa] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen || !invoice || !config) return null;

  const handleCancel = async () => {
    if (justificativa.length < 15) {
      setError("A justificativa precisa ter no mínimo 15 caracteres.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // 1. Impedir cancelamento de Nota de Entrada de Garantia se houver Envio (Saída) de Garantia ativo
      if (invoice.tipo_nota === "garantia_entrada") {
        const activeExitNotes = await pb.collection("fiscal_invoices").getList(1, 1, {
          filter: `tipo_nota = "garantia_saida" && venda_original = "${invoice.venda_original || invoice.id}" && nfe_status != "cancelada" && nfe_status != "erro"`,
        });
        if (activeExitNotes.items.length > 0) {
          throw new Error("Não é possível cancelar a Entrada de Garantia pois existe uma nota de Envio (Saída) de Garantia ativa vinculada a ela. Cancele a nota de Envio de Garantia primeiro.");
        }
      }

      // 2. Impedir cancelamento de Nota de Venda Original se houver Devolução ou Entrada de Garantia ativa vinculada
      const isSale = !invoice.tipo_nota || invoice.tipo_nota === "venda";
      if (isSale) {
        const activeChildNotes = await pb.collection("fiscal_invoices").getList(1, 1, {
          filter: `venda_original = "${invoice.id}" && nfe_status != "cancelada" && nfe_status != "erro"`,
        });
        if (activeChildNotes.items.length > 0) {
          const childType = activeChildNotes.items[0].tipo_nota === "devolucao" ? "Devolução" : "Garantia";
          throw new Error(`Não é possível cancelar a nota de venda original pois existe uma nota de ${childType} ativa vinculada a ela. Cancele a nota de ${childType} primeiro.`);
        }
      }

      const nfeAmbiente = invoice.ambiente || invoice.full_data?.ambiente || config.ambiente || "homologacao";
      const token = (nfeAmbiente === "producao" ? config.token_producao : config.token_homologacao || "").trim();
      
      // A referência para Focus é o ID do registro no PocketBase (ou com prefixo se for devolução/garantia)
      let focusRef = invoice.id;

      const payload = {
        ref: focusRef,
        justificativa: justificativa.trim(),
        ambiente: nfeAmbiente,
        token: token,
        pb_token: pb.authStore.token,
      };

      const result = await fiscalService.cancelInvoice(payload);
      
      // Atualiza o registro no banco local
      await pb.collection("fiscal_invoices").update(invoice.id, {
        nfe_status: "cancelada",
        nfe_cancelamento_justificativa: justificativa.trim(),
        nfe_cancelamento_retorno: result,
      });

      alert("Nota fiscal cancelada com sucesso!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || "Erro desconhecido ao tentar cancelar nota.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 p-4 flex justify-between items-center text-white">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} />
            <h3 className="font-bold text-sm uppercase tracking-wide">
              Cancelar NF-e #{invoice.nfe_numero || invoice.order_number}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 p-1.5 rounded-full transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 space-y-1">
            <span className="font-extrabold block">Atenção!</span>
            <p>
              O cancelamento de uma nota fiscal é uma ação irreversível na SEFAZ. O prazo máximo para cancelamento padrão é de 24 horas após a autorização.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
              Justificativa do Cancelamento (Mín. 15 caracteres)
            </label>
            <textarea
              className="w-full p-3 border rounded-xl text-xs outline-none focus:border-red-500 font-medium h-24 resize-none shadow-inner"
              placeholder="Digite aqui o motivo do cancelamento. Ex: Erro administrativo no faturamento do pedido..."
              value={justificativa}
              onChange={(e) => {
                setJustificativa(e.target.value);
                if (e.target.value.length >= 15) setError("");
              }}
            />
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-400">
              <span>{justificativa.length} / 15 caracteres mínimo</span>
              {justificativa.length < 15 && justificativa.length > 0 && (
                <span className="text-red-500">Faltam {15 - justificativa.length} caracteres</span>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-500 font-bold hover:bg-slate-200 rounded-lg text-xs"
            disabled={loading}
          >
            Fechar
          </button>
          <button
            onClick={handleCancel}
            disabled={loading || justificativa.length < 15}
            className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg flex items-center gap-1.5 text-xs shadow-md shadow-red-200 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Cancelando...
              </>
            ) : (
              <>
                <Send size={14} />
                Confirmar Cancelamento
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
